/**
 * CostPerStoryPointSection — View A "Cost per Story Point" (RAISE-16869 D6).
 *
 * BETA (experimental) card: KPI = ratio of sums for the current calendar
 * month, trend vs prior month, exclusion notice (zero/no-SP + unmatched),
 * and an epic-level comparison table that renders only once >=1 included
 * story carries a non-null epic (RAISE-16867 D6 precedent).
 *
 * State precedence: planGated -> error -> loading -> empty -> data.
 * Props, not internal fetches — sibling testability pattern shared with
 * EpicBreakdownSection / DeveloperBreakdownSection.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  indexCalibration,
  joinCostWithSp,
  type CalibrationEntry,
  type EpicCostSp,
} from "@/lib/cost/costPerStoryPoint";
import type { StoryCostEvent } from "@/lib/cost/storyCostEvents";
import { formatCost } from "@/lib/format";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CostPerStoryPointSectionProps {
  events: StoryCostEvent[]; // UNFILTERED — ignores the page PeriodFilter (D2)
  eventsLoading: boolean;
  eventsError: string | null;
  planGated: boolean;
  calibration: CalibrationEntry[];
  calibrationLoading: boolean;
  calibrationError: string | null;
  failedProjects: number;
}

type SortKey = "epic" | "stories" | "totalCost" | "totalSp" | "costPerSp";
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

function sortEpics(rows: EpicCostSp[], key: SortKey, dir: SortDir): EpicCostSp[] {
  return [...rows].sort((a, b) => {
    const cmp = key === "epic" ? a.epic.localeCompare(b.epic) : a[key] - b[key];
    return dir === "asc" ? cmp : -cmp;
  });
}

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
}

function SortHeader({ label, sortKey, currentKey, currentDir, onSort }: SortHeaderProps) {
  const active = currentKey === sortKey;
  return (
    <TableHead className="cursor-pointer select-none" onClick={() => onSort(sortKey)}>
      <span className="flex items-center gap-1">
        {label}
        {active &&
          (currentDir === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          ))}
      </span>
    </TableHead>
  );
}

function formatDelta(delta: number): string {
  const sign = delta >= 0 ? "+" : "−";
  return `${sign}${formatCost(Math.abs(delta))}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CostPerStoryPointSection({
  events,
  eventsLoading,
  eventsError,
  planGated,
  calibration,
  calibrationLoading,
  calibrationError,
  failedProjects,
}: CostPerStoryPointSectionProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>("costPerSp");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const result = useMemo(() => {
    const now = new Date();
    const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const index = indexCalibration(calibration);
    return joinCostWithSp(events, index, todayLocal);
  }, [events, calibration]);

  const epicRows = useMemo(
    () => sortEpics(result.epics, sortKey, sortDir),
    [result.epics, sortKey, sortDir],
  );

  const loading = eventsLoading || calibrationLoading;
  const error = eventsError ?? calibrationError;
  const isEmpty = calibration.length === 0 || result.included.length === 0;

  const trend =
    result.currentMonth && result.priorMonth
      ? result.currentMonth.costPerSp - result.priorMonth.costPerSp
      : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-semibold">
            {t("costPage.costPerSp.title")}
          </CardTitle>
          <Badge
            variant="outline"
            className="border-amber-500/50 text-amber-600 dark:text-amber-400"
          >
            {t("costPage.costPerSp.beta")}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("costPage.costPerSp.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        {planGated ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.costPerSp.planGate")}
          </p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isEmpty ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.costPerSp.empty")}
          </p>
        ) : (
          <>
            <div className="mb-4 rounded-lg border p-4">
              <p className="text-xs font-medium text-muted-foreground">
                {t("costPage.costPerSp.kpiLabel")}
              </p>
              <p className="mt-1 text-2xl font-bold">
                {result.currentMonth
                  ? `${formatCost(result.currentMonth.costPerSp)}/SP`
                  : "—"}
              </p>
              <p
                className={`mt-1 text-xs ${
                  trend === null
                    ? "text-muted-foreground"
                    : trend <= 0
                      ? "text-[#6fa08c]"
                      : "text-[#e0664a]"
                }`}
              >
                {trend !== null && result.priorMonth
                  ? t("costPage.costPerSp.trendVs", {
                      delta: formatDelta(trend),
                      month: result.priorMonth.month,
                    })
                  : t("costPage.costPerSp.trendNa")}
              </p>
            </div>

            {(result.excludedZeroSp > 0 || result.unmatched > 0) && (
              <p className="mb-3 text-xs text-muted-foreground">
                {t("costPage.costPerSp.excludedNote", {
                  zeroSp: result.excludedZeroSp,
                  unmatched: result.unmatched,
                })}
              </p>
            )}

            {failedProjects > 0 && (
              <p className="mb-3 text-xs text-muted-foreground">
                {t("costPage.costPerSp.failedProjectsNote", {
                  count: failedProjects,
                })}
              </p>
            )}

            {result.epics.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHeader
                      label={t("costPage.costPerSp.columnEpic")}
                      sortKey="epic"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={t("costPage.costPerSp.columnStories")}
                      sortKey="stories"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={t("costPage.costPerSp.columnCost")}
                      sortKey="totalCost"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={t("costPage.costPerSp.columnSp")}
                      sortKey="totalSp"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={t("costPage.costPerSp.columnCostPerSp")}
                      sortKey="costPerSp"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {epicRows.map((row) => (
                    <TableRow key={row.epic}>
                      <TableCell className="font-mono text-xs">{row.epic}</TableCell>
                      <TableCell className="text-xs">{row.stories}</TableCell>
                      <TableCell className="text-xs font-medium">
                        {formatCost(row.totalCost)}
                      </TableCell>
                      <TableCell className="text-xs">{row.totalSp}</TableCell>
                      <TableCell className="text-xs font-medium">
                        {formatCost(row.costPerSp)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("costPage.costPerSp.epicTableNote")}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
